import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Copy, Loader2, Send, X } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { kitShare, kitShareCancel, kitShares } from "../../lib/api";

const copy = (t, what) => navigator.clipboard?.writeText(t).then(() => toast.success(`${what} copied`)).catch(() => {});

// Share the emergency kit: link by text/email, 6-digit code shown here to pass on separately, 72-hour expiry, cancel any time.
export const KitShareCard = ({ count }) => {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["kit-shares"], queryFn: kitShares });
  const [f, setF] = useState({ name: "", phone: "", email: "" });
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try { const r = await kitShare(f); setRes(r); setF({ name: "", phone: "", email: "" }); qc.invalidateQueries({ queryKey: ["kit-shares"] }); }
    catch (e) { const d = e?.response?.data?.detail; toast.error(typeof d === "string" ? d : "Couldn't share"); } finally { setBusy(false); }
  };
  const cancel = async (id) => { await kitShareCancel(id); qc.invalidateQueries({ queryKey: ["kit-shares"] }); toast.success("Link cancelled"); };
  const active = (data?.shares || []).filter((s) => s.status === "active");
  return (
    <div className="mt-4 rounded-2xl border border-border bg-card p-4" data-testid="kit-share-card">
      <div className="text-sm font-medium">Share the kit with someone you trust</div>
      <p className="text-xs text-muted-foreground">They get a link by text (or email). You give them the 6-digit code yourself. It stops working after 72 hours.</p>
      {count === 0 ? <p className="mt-2 text-xs text-muted-foreground">Add a document to the kit first.</p> : (
        <div className="mt-3 grid sm:grid-cols-4 gap-2">
          <Input placeholder="Their name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="h-9" data-testid="kit-share-name" />
          <Input placeholder="Mobile number" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="h-9" data-testid="kit-share-phone" />
          <Input placeholder="or email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="h-9" data-testid="kit-share-email" />
          <Button size="sm" className="h-9 rounded-full gap-1.5" onClick={send} disabled={busy || !f.name.trim() || !(f.phone.trim() || f.email.trim())} data-testid="kit-share-send">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send link</Button>
        </div>
      )}
      {res && (
        <div className="mt-3 rounded-xl border border-primary/40 bg-primary/5 p-3 text-sm" data-testid="kit-share-result">
          <div>{res.sent === "sms" ? "Link texted." : res.sent === "email" ? "Link emailed." : "Couldn't send it — copy the link below and send it yourself."} Now give them this code another way (call or say it):</div>
          <div className="mt-2 flex items-center gap-3"><span className="font-mono text-2xl tracking-[0.3em]" data-testid="kit-share-code-value">{res.code}</span>
            <Button size="sm" variant="ghost" className="gap-1" onClick={() => copy(res.code, "Code")} data-testid="kit-share-copy-code"><Copy className="h-3.5 w-3.5" /> Code</Button>
            <Button size="sm" variant="ghost" className="gap-1" onClick={() => copy(res.link, "Link")} data-testid="kit-share-copy-link"><Copy className="h-3.5 w-3.5" /> Link</Button></div>
          <Input readOnly value={res.link} onFocus={(e) => e.target.select()} className="mt-2 h-8 text-xs font-mono" data-testid="kit-share-link-value" />
          <div className="text-[11px] text-muted-foreground mt-1">The code is only shown now.</div>
        </div>
      )}
      {active.length > 0 && (
        <ul className="mt-3 space-y-1.5" data-testid="kit-share-list">
          {active.map((s) => (
            <li key={s.id} className="flex items-center gap-2 text-xs rounded-lg border border-border px-2.5 py-1.5" data-testid={`kit-share-row-${s.id}`}>
              <span className="flex-1"><b>{s.name}</b> · {s.phone || s.email} · expires {new Date(s.expires_at).toLocaleString()} · {s.views ? `opened ${s.views}×` : "not opened yet"}</span>
              <button type="button" onClick={() => cancel(s.id)} className="text-destructive inline-flex items-center gap-1 hover:underline" data-testid={`kit-share-cancel-${s.id}`}><X className="h-3 w-3" /> Cancel</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
